import type { ProfileConfig } from "@worker/config-schema";

/**
 * Audio visualizer. Multiple render modes share the same analyser pipeline.
 *
 * A note on pipeline sharing: `createMediaElementSource` can only be called
 * once per <audio> element. If the preview iframe remounts the audio player
 * while the visualizer is active, we'd double-instantiate and throw. We
 * dedupe via a WeakMap so the pipeline is created at most once per audio
 * element, and subsequent mounts reuse the analyser.
 */

type AudioPipeline = {
  ctx: AudioContext;
  source: MediaElementAudioSourceNode;
  analyser: AnalyserNode;
};
const pipelines = new WeakMap<HTMLAudioElement, AudioPipeline>();

export type VizConfig = Pick<
  ProfileConfig["audio"],
  | "visualizerStyle"
  | "visualizerScale"
  | "visualizerColor"
  | "visualizerColor2"
  | "visualizerIntensity"
  | "visualizerBaseline"
>;

export function attachVisualizer(
  audio: HTMLAudioElement,
  canvas: HTMLCanvasElement,
  cfg: VizConfig,
): () => void {
  let raf = 0;
  let pipe: AudioPipeline | null = null;
  // Per-frame smoothed values (plasma-wave). Sized lazily on first draw.
  let smoothed: Float32Array = new Float32Array(0);

  const init = () => {
    if (pipe) return;
    const cached = pipelines.get(audio);
    if (cached) {
      pipe = cached;
      return;
    }
    try {
      const ctx = new AudioContext();
      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      // Waveform benefits from more samples for a smooth line; bars from a
      // balanced resolution. 512 FFT gives us 256 bins — enough to log-map.
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.82;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      pipe = { ctx, source, analyser };
      pipelines.set(audio, pipe);
    } catch {
      pipe = null;
    }
  };

  // Read --viz-color from the canvas's inline style each frame so a
  // cover-color extraction result (set via `canvas.style.setProperty`)
  // takes effect immediately. Falls back to the admin-configured color.
  const primaryColor = () =>
    canvas.style.getPropertyValue("--viz-color").trim() ||
    cfg.visualizerColor;

  const buildFill = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
    const primary = primaryColor();
    if (cfg.visualizerColor2) {
      const g = ctx.createLinearGradient(0, h, 0, 0);
      g.addColorStop(0, primary);
      g.addColorStop(1, cfg.visualizerColor2);
      return g;
    }
    return primary;
  };

  /**
   * Walk the perimeter of a rounded rectangle (inset by `padding`, with
   * corner radius `r`) and return N equally-spaced points along it with
   * outward-facing unit normals. Used by the border visualizer to place
   * (Border-extent visualizer + tracePerimeter helper were removed — they
   * never traced the rounded rectangle cleanly and the card/background
   * extents cover the same use case more reliably.)
   */

  /** Remap `freq[bin]` → value at x across `w` width, log-scaled. */
  const logIndex = (x: number, w: number, bins: number): number => {
    // x in [0, w) → exponent in [0, 1). Use log2 mapping so we cover a
    // realistic 20Hz–20kHz-like distribution across the canvas.
    const t = x / w;
    const minExp = Math.log2(2);        // ~ bin 2
    const maxExp = Math.log2(bins - 1); // top bin
    const exp = minExp + t * (maxExp - minExp);
    return Math.min(bins - 1, Math.max(0, Math.floor(Math.pow(2, exp))));
  };

  const draw = () => {
    if (!pipe) return;
    const c = canvas.getContext("2d");
    if (!c) return;

    const w = (canvas.width = canvas.offsetWidth || canvas.width);
    const h = (canvas.height = canvas.offsetHeight || canvas.height);
    c.clearRect(0, 0, w, h);
    c.globalAlpha = 0.3 + 0.65 * cfg.visualizerIntensity;

    const fillStyle = buildFill(c, w, h);

    // Baseline Y: where bars/waves "stand". 100% means bottom of canvas,
    // 0% top, 50% middle. Defaults to 100 (classic bottom-anchored bars).
    const baseline = (cfg.visualizerBaseline ?? 100) / 100;
    const baseY = h * baseline;

    if (cfg.visualizerStyle === "waveform" || cfg.visualizerStyle === "filled") {
      // Plasma-wave: instead of drawing raw time-domain samples at audio
      // rate (twitchy), we down-sample frequency bands into a small number
      // of control points, lerp against the previous frame for smoothing,
      // and connect them with quadratic bezier curves. Reads like a
      // flowing wave reacting to music, not scope jitter.
      const points = 32;
      if (smoothed.length !== points) {
        smoothed = new Float32Array(points);
      }
      const buf = new Uint8Array(pipe.analyser.frequencyBinCount);
      pipe.analyser.getByteFrequencyData(buf);
      for (let i = 0; i < points; i++) {
        const bin = logIndex(i / points * w, w, buf.length);
        const target = (buf[bin] ?? 0) / 255;
        // Exponential smoothing. 0.18 new + 0.82 old = ~80ms settle.
        smoothed[i] = smoothed[i]! * 0.82 + target * 0.18;
      }
      const step = w / (points - 1);
      const amp = (h / 2) * (0.35 + 1.0 * cfg.visualizerIntensity);

      // Waveform oscillates around the baseline Y. Sample values center
      // on the baseline and deflect up/down by the smoothed magnitude.
      const yAt = (i: number) => baseY - smoothed[i]! * amp;

      c.strokeStyle = fillStyle;
      c.fillStyle = fillStyle;
      c.lineWidth = 2.5;
      c.lineJoin = "round";
      c.beginPath();
      c.moveTo(0, yAt(0));
      for (let i = 0; i < points - 1; i++) {
        const cx = (i * step + (i + 1) * step) / 2;
        const cy = (yAt(i) + yAt(i + 1)) / 2;
        c.quadraticCurveTo(i * step, yAt(i), cx, cy);
      }
      c.lineTo((points - 1) * step, yAt(points - 1));
      if (cfg.visualizerStyle === "filled") {
        // Fill extends from the wave path down to the baseline-anchored
        // edge. Bottom-anchored (default) fills downward; top-anchored
        // fills upward; middle fills both directions symmetrically.
        const fillFloor = baseline >= 0.5 ? h : 0;
        c.lineTo(w, fillFloor);
        c.lineTo(0, fillFloor);
        c.closePath();
        c.fill();
      } else {
        c.stroke();
      }
    } else if (cfg.visualizerStyle === "ring") {
      // Radial bars around canvas center. Used when the visualizer extent
      // is set to wrap a round avatar (the CSS layer positions the canvas
      // as a square overlapping the avatar, so bars radiate outward).
      const buf = new Uint8Array(pipe.analyser.frequencyBinCount);
      pipe.analyser.getByteFrequencyData(buf);
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) / 2 - 6;
      const barCount = 72;
      c.strokeStyle = fillStyle;
      c.lineWidth = 2;
      c.lineCap = "round";
      for (let i = 0; i < barCount; i++) {
        const bin = cfg.visualizerScale === "log"
          ? logIndex(i / barCount * w, w, buf.length)
          : Math.floor((i / barCount) * buf.length);
        const v = (buf[bin] ?? 0) / 255;
        const len = 4 + v * 28 * (0.5 + cfg.visualizerIntensity);
        const angle = (i / barCount) * Math.PI * 2 - Math.PI / 2;
        const x1 = cx + Math.cos(angle) * radius;
        const y1 = cy + Math.sin(angle) * radius;
        const x2 = cx + Math.cos(angle) * (radius + len);
        const y2 = cy + Math.sin(angle) * (radius + len);
        c.beginPath();
        c.moveTo(x1, y1);
        c.lineTo(x2, y2);
        c.stroke();
      }
    } else {
      const buf = new Uint8Array(pipe.analyser.frequencyBinCount);
      pipe.analyser.getByteFrequencyData(buf);
      const barCount = cfg.visualizerStyle === "dots" ? 48 : 64;
      const bw = w / barCount;
      c.fillStyle = fillStyle;
      for (let i = 0; i < barCount; i++) {
        const bin = cfg.visualizerScale === "log"
          ? logIndex(i / barCount * w, w, buf.length)
          : Math.floor((i / barCount) * buf.length);
        const v = (buf[bin] ?? 0) / 255;
        const bh = v * h * (0.45 + 0.85 * cfg.visualizerIntensity);
        if (cfg.visualizerStyle === "dots") {
          // Dots sit at the bar tips, floating above / below the baseline.
          const cx = i * bw + bw / 2;
          const cy = baseY - bh - 2;
          c.beginPath();
          c.arc(cx, cy, 2 + v * 2, 0, Math.PI * 2);
          c.fill();
        } else {
          // Mirror bars grow both up AND down from the baseline.
          // (This is the only remaining per-frequency bar style — the
          // classic "bars" style was removed.)
          const half = bh / 2;
          c.fillRect(i * bw, baseY - half, bw * 0.8, half);
          c.fillRect(i * bw, baseY, bw * 0.8, half);
        }
      }
    }
    c.globalAlpha = 1;
    raf = requestAnimationFrame(draw);
  };

  const start = async () => {
    init();
    if (!pipe) return;
    if (pipe.ctx.state === "suspended") {
      try { await pipe.ctx.resume(); } catch { /* ignored */ }
    }
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(draw);
  };

  audio.addEventListener("play", start);
  // On pause, keep the RAF loop running — the analyser continues to report
  // frequencies, and with smoothingTimeConstant = 0.82 bars will naturally
  // decay to 0 over ~1s once audio signal stops. Freezing mid-animation
  // looks broken, so we let the decay play out.
  if (!audio.paused) void start();
  // Kick off the loop on mount so bars are ready to decay even if the
  // user hasn't pressed play yet.
  start();

  return () => {
    cancelAnimationFrame(raf);
    audio.removeEventListener("play", start);
    // Pipeline stays cached for the lifetime of the <audio> element so the
    // next mount can reuse it without a re-create (which would throw).
  };
}
